import { useByRegion, useDistribution, useSummary } from '../api/hooks';
import { BarChart } from '../charts/BarChart';
import { Histogram } from '../charts/Histogram';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { KpiCard } from '../components/KpiCard';
import { Panel } from '../components/Panel';
import { fmtInt, fmtValueParts } from '../lib/format';
import { isEmptySelection } from '../state/filters';
import { PageShell, usePage } from './PageShell';
import { t } from '../i18n';

export function Overview() {
  const { state, query } = usePage();
  const empty = state ? isEmptySelection(state) : false;
  const q = empty ? null : query;
  const summary = useSummary(q);
  const region = useByRegion(q);
  const dist = useDistribution(q);

  const s = summary.data;
  const median = fmtValueParts(s?.units, s?.median_ref_year);
  const refNote = s?.ref_year ? t.refYear(s.ref_year) : undefined;
  const loading = summary.isPending && !empty;

  return (
    <PageShell title={t.overviewTitle} note={state?.indicator}>
      <section className="section kpis">
        {summary.isError ? <ErrorState error={summary.error} /> : (
          <div className="score four">
            <KpiCard name={t.kpiUtilities} value={fmtInt(s?.utilities)} loading={loading} sub={t.inSelection} />
            <KpiCard name={t.kpiCountries} value={fmtInt(s?.countries)} loading={loading} sub={t.inSelection} />
            <KpiCard name={t.kpiMedian} value={median.text} unit={median.unit} loading={loading} sub={refNote ?? t.noRefYear} />
            <KpiCard name={t.kpiObservations} value={fmtInt(s?.observations)} loading={loading} sub={t.utilityYears} />
          </div>
        )}
      </section>

      <div className="cols-7-5">
        <Panel title={t.medianByRegion} note={refNote}>
          {region.isError ? <ErrorState error={region.error} /> :
           region.isPending && !empty ? <div className="skeleton" style={{ height: 300 }} /> :
           !region.data?.rows.length || empty ? <EmptyState /> : (
            <BarChart
              rows={region.data.rows.map((r) => ({ key: r.region, label: r.label, value: r.median, n: r.n }))}
              units={region.data.units}
              valueLabel={t.medianIn(region.data.ref_year)}
            />
          )}
        </Panel>

        <Panel
          title={t.distribution}
          note={dist.data && dist.data.n_clipped > 0 ? t.outliers(dist.data.n_clipped) : (dist.data?.n ? t.nUtilities(fmtInt(dist.data.n)) : undefined)}
        >
          {dist.isError ? <ErrorState error={dist.error} /> :
           dist.isPending && !empty ? <div className="skeleton" style={{ height: 300 }} /> :
           !dist.data?.bins.length || empty ? <EmptyState /> : <Histogram data={dist.data} />}
        </Panel>
      </div>
    </PageShell>
  );
}
